import {
    DropTarget,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { ContainerDropType } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/dropTypes';
import {
    ActionInstruction,
    DropReducer,
    ReducerState,
    StoreAction,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/types';
import {
    DropTargetPayload,
    SeatingTicket,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import { indexToSeatNumber } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';

const containerDropReducer: DropReducer = (state, action) => {
    const { activeData, overData, selectedTicketIds = [] } = action.payload;

    if (!isValidContainerTarget(overData)) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'Invalid drop target' },
        };
    }

    const ticketIds = extractTicketIds(activeData, selectedTicketIds);
    if (ticketIds.length === 0) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'No tickets selected' },
        };
    }

    const scenario = determineContainerScenario(state, ticketIds);

    switch (scenario.type) {
        case ContainerDropType.MULTIPLE_WITH_GROUPS:
        case ContainerDropType.SINGLE_WITH_GROUP:
            return handleGroupToUnseated(scenario.ticketIds, overData, state);

        case ContainerDropType.MULTIPLE_WITHOUT_GROUPS:
        case ContainerDropType.SINGLE_WITHOUT_GROUP:
            return {
                action: StoreAction.RESHUFFLE_AND_SEAT,
                payload: {
                    ticketIds: scenario.ticketIds,
                    tableId: UNSEATED_GROUP_ID,
                    seatNumber: indexToSeatNumber(getLastUnseatedPosition(state)),
                },
            };

        default:
            return {
                action: StoreAction.SHOW_ERROR,
                payload: { message: 'Invalid drop action' },
            };
    }
};

const determineContainerScenario = (state: ReducerState, ticketIds: string[]) => {
    if (ticketIds.length > 1) {
        const hasGroups = hasAnyTicketGroups(ticketIds, state.tickets);
        return {
            type: hasGroups
                ? ContainerDropType.MULTIPLE_WITH_GROUPS
                : ContainerDropType.MULTIPLE_WITHOUT_GROUPS,
            ticketIds,
        };
    }

    const ticket = state.tickets[ticketIds[0]];
    if (!ticket) {
        return { type: ContainerDropType.INVALID, ticketIds };
    }

    return {
        type: ticket.ticket_group_id
            ? ContainerDropType.SINGLE_WITH_GROUP
            : ContainerDropType.SINGLE_WITHOUT_GROUP,
        ticketIds,
    };
};

const handleGroupToUnseated = (
    ticketIds: string[],
    overData: DropTargetPayload,
    state: ReducerState,
): ActionInstruction => {
    if (ticketIds.length === 0) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'No tickets selected' },
        };
    }

    const firstTicket = state.tickets[ticketIds[0]];
    const targetSeat = findTargetSeatForGroup(firstTicket, ticketIds, overData, state);

    return {
        action: StoreAction.RESHUFFLE_AND_SEAT,
        payload: {
            ticketIds,
            tableId: UNSEATED_GROUP_ID,
            seatNumber: targetSeat,
        },
    };
};

const isValidContainerTarget = (overData: DropTargetPayload): boolean => {
    return (
        overData.type === DropTarget.UNSEATED_CONTAINER ||
        overData.type === DropTarget.SEATING_CONTAINER
    );
};

const extractTicketIds = (activeData: DropTargetPayload, selectedTicketIds: string[]): string[] => {
    if (activeData.type === DropTarget.TICKET && activeData.ticketId) {
        return [activeData.ticketId, ...selectedTicketIds.filter(id => id !== activeData.ticketId)];
    }
    return selectedTicketIds;
};

const hasAnyTicketGroups = (
    ticketIds: string[],
    tickets: Record<string, SeatingTicket>,
): boolean => {
    return ticketIds.some(id => tickets[id]?.ticket_group_id);
};

const findTargetSeatForGroup = (
    firstTicket: SeatingTicket | undefined,
    ticketIds: string[],
    overData: DropTargetPayload,
    state: ReducerState,
): number => {
    if (!firstTicket?.ticket_group_id) {
        return indexToSeatNumber(getLastUnseatedPosition(state));
    }

    const existingGroupMembers = Object.values(state.tickets)
        .filter(
            t =>
                t.ticket_group_id === firstTicket.ticket_group_id &&
                t.seating_group_id === UNSEATED_GROUP_ID &&
                !ticketIds.includes(t.id),
        )
        .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));

    if (existingGroupMembers.length === 0) {
        return indexToSeatNumber(getLastUnseatedPosition(state));
    }

    const lastMember = existingGroupMembers[existingGroupMembers.length - 1];
    return indexToSeatNumber(lastMember.seat_number || 0);
};

const getLastUnseatedPosition = (state: ReducerState): number => {
    const unseatedTickets = Object.values(state.tickets)
        .filter(t => t.seating_group_id === UNSEATED_GROUP_ID)
        .sort((a, b) => (b.seat_number || 0) - (a.seat_number || 0));

    return unseatedTickets.length > 0 ? unseatedTickets[0].seat_number || 0 : 0;
};

export { containerDropReducer };
